import { Link } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import {
  KeepExploring,
  RelatedComparisons,
  RelatedUseCases,
} from "../components/RelatedLinks";
import { useLang, useLp } from "../lib/i18n";
import { salesEngineerDiscoveryCopy } from "../lib/translations/pages/sales-engineer-discovery";

export function SalesEngineerDiscovery() {
  const lang = useLang();
  const lp = useLp();
  const t = salesEngineerDiscoveryCopy[lang];

  return (
    <div className="min-h-screen flex flex-col">
      <Seo
        title={t.seo.title}
        description={t.seo.description}
        path="/use-cases/sales-engineer-discovery-call"
      />
      <Header />

      <main className="flex-1">
        {/* Hero */}
        <section className="border-b border-margin dark:border-paper/10">
          <div className="mx-auto max-w-3xl px-6 pt-20 pb-16 sm:pt-28 sm:pb-20">
            <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink/55 dark:text-paper/55">
              {t.eyebrow}
            </p>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl font-semibold tracking-tight text-ink dark:text-paper">
              {t.h1}
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-ink/75 dark:text-paper/75">
              {t.lede}
            </p>
          </div>
        </section>

        {/* Problem */}
        <Reveal>
          <section className="border-b border-margin dark:border-paper/10">
            <div className="mx-auto max-w-3xl px-6 py-20">
              <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-ink dark:text-paper">
                {t.problem.heading}
              </h2>
              <p className="mt-6 text-[15px] leading-relaxed text-ink/80 dark:text-paper/80">
                {t.problem.body1}
              </p>
              <p className="mt-4 text-[15px] leading-relaxed text-ink/80 dark:text-paper/80">
                {t.problem.body2}
              </p>
            </div>
          </section>
        </Reveal>

        {/* Scenarios */}
        <Reveal>
          <section className="border-b border-margin dark:border-paper/10">
            <div className="mx-auto max-w-5xl px-6 py-20">
              <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-ink dark:text-paper">
                {t.scenarios.heading}
              </h2>
              <div className="mt-10 grid gap-6 md:grid-cols-3">
                {t.scenarios.items.map((item) => (
                  <article
                    key={item.title}
                    className="rounded-2xl border border-ink/10 dark:border-paper/12 p-6 bg-ink/[0.02] dark:bg-paper/[0.02]"
                  >
                    <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink/55 dark:text-paper/55">
                      {item.tag}
                    </p>
                    <h3 className="mt-3 font-display text-xl font-semibold text-ink dark:text-paper">
                      {item.title}
                    </h3>
                    <p className="mt-3 text-[14px] leading-relaxed text-ink/75 dark:text-paper/75">
                      {item.body}
                    </p>
                  </article>
                ))}
              </div>
            </div>
          </section>
        </Reveal>

        {/* How */}
        <Reveal>
          <section className="border-b border-margin dark:border-paper/10">
            <div className="mx-auto max-w-3xl px-6 py-20">
              <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-ink dark:text-paper">
                {t.how.heading}
              </h2>
              <p className="mt-6 text-[15px] leading-relaxed text-ink/80 dark:text-paper/80">
                {t.how.body}
              </p>
              <ul className="mt-8 space-y-4">
                {t.how.bullets.map((b) => (
                  <li
                    key={b}
                    className="text-[15px] leading-relaxed text-ink/80 dark:text-paper/80"
                  >
                    {b}
                  </li>
                ))}
              </ul>
            </div>
          </section>
        </Reveal>

        {/* Why over notetakers */}
        <Reveal>
          <section className="border-b border-margin dark:border-paper/10">
            <div className="mx-auto max-w-3xl px-6 py-20">
              <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-ink dark:text-paper">
                {t.proof.heading}
              </h2>
              <ul className="mt-8 space-y-4">
                {t.proof.bullets.map((b) => (
                  <li
                    key={b}
                    className="text-[15px] leading-relaxed text-ink/80 dark:text-paper/80"
                  >
                    {b}
                  </li>
                ))}
              </ul>
            </div>
          </section>
        </Reveal>

        {/* Related use-cases + comparisons (graph-driven) */}
        <Reveal>
          <RelatedUseCases currentId="use-case-sales-engineer-discovery" />
        </Reveal>
        <Reveal>
          <RelatedComparisons currentId="use-case-sales-engineer-discovery" />
        </Reveal>

        {/* CTA */}
        <Reveal>
          <section>
            <div className="mx-auto max-w-3xl px-6 py-24 text-center">
              <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-ink dark:text-paper">
                {t.cta.heading}
              </h2>
              <p className="mt-5 text-lg text-ink/70 dark:text-paper/70">
                {t.cta.body}
              </p>
              <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
                <Link
                  to={lp + "/?auth=signup&plan=free"}
                  className="inline-flex items-center rounded-full bg-ink px-6 py-3 text-sm font-medium text-paper hover:bg-ink/90 transition-colors dark:bg-paper dark:text-ink dark:hover:bg-paper/90"
                >
                  {t.cta.primary}
                </Link>
                <Link
                  to={lp + "/downloads"}
                  className="inline-flex items-center rounded-full border border-ink/15 dark:border-paper/15 px-6 py-3 text-sm font-medium text-ink dark:text-paper hover:bg-ink/5 dark:hover:bg-paper/5 transition-colors"
                >
                  {t.cta.secondary}
                </Link>
              </div>
            </div>
          </section>
        </Reveal>

        {/* Keep exploring (ensures the page hits the body internal-link minimum
             even when sibling comparison/use-case routes are still unpublished) */}
        <KeepExploring />
      </main>

      <Footer />
    </div>
  );
}
